import type { Notification } from './types.ts'

/** What the reader is being asked to do, shown as a button on the row. */
export interface NotificationAction {
  label: string
  /** Where the action lives — the notification's own link, or its detail page when the answer is given there. */
  to: string
}

// Kinds that ask the reader to do something. The answer for team invites and
// side swaps is given on the notification's detail page; the rest live at href.
const actionLabels: Record<string, string> = {
  team_invite: 'Respond',
  org_invite: 'Respond',
  side_swap_request: 'Respond',
  competition_entry_received: 'Review',
  competition_entry_leave_requested: 'Rule on it',
  free_agent_request_received: 'Review',
  free_agent_recommended: 'Respond',
  guest_team_owner_requested: 'Review',
  guest_team_claimable: 'Claim',
  guest_team_ownable: 'Take ownership',
  team_contact_league_request: 'Choose contact',
  team_contact_method_requested: 'Add contact',
  riot_link_request: 'Link account',
  payment_reminder: 'Pay',
}

const answeredOnDetail = new Set(['team_invite', 'side_swap_request'])

export function notificationAction(n: Notification): NotificationAction | null {
  const label = actionLabels[n.type]
  if (!label) return null
  const detail = `/notifications/${n.id}`
  if (answeredOnDetail.has(n.type) || !n.href) return { label, to: detail }
  return { label, to: n.href }
}

export type NotificationTab = 'all' | 'needs-you' | 'competitions' | 'teams' | 'carousel'

const competitionPrefixes = ['match_', 'season_', 'competition_', 'free_agent_', 'league_']
const competitionKinds = new Set(['schedule_announcement', 'side_swap_request', 'riot_link_request'])
const teamPrefixes = ['team_', 'roster_', 'guest_team_', 'org_']

/** Which filter tab a notification belongs to (besides All, and Needs you when it asks for action). */
export function notificationCategory(n: Notification): Exclude<NotificationTab, 'all' | 'needs-you'> | null {
  // Platform notices carry no org.
  if (!n.org.name) return 'carousel'
  const kind: string = n.type
  if (competitionKinds.has(kind) || competitionPrefixes.some((p) => kind.startsWith(p))) return 'competitions'
  if (teamPrefixes.some((p) => kind.startsWith(p))) return 'teams'
  return null
}

export function inTab(n: Notification, tab: NotificationTab): boolean {
  if (tab === 'all') return true
  if (tab === 'needs-you') return notificationAction(n) !== null
  return notificationCategory(n) === tab
}
